import { useState, useEffect, useRef } from "react";

import "react-awesome-lightbox/build/style.css";

import { 
  Typography, 
  CircularProgress, 
  Button, 
  Box, 
  useTheme,
  useMediaQuery
} from "@mui/material";


import Dish from "./components/Dish";

import { useSelector, useDispatch } from "react-redux";
import {
  fetchMenuCategorySuccess,
  selectedCategory,
  updateHideDishes,
} from "../../store/actions/menu";
import {
  setRestaurantId,
  loadPreviousCart,
  updateMemberId,
} from "../../store/actions/order";

import DynamicCategories from "./components/DynamicCategories";
import CategorySections from "./components/CategorySections";

import { filterVisibility, decodeRestaurantId } from "util/utils";
import { database, ref, onValue } from "util/firebase";

import { ScrollingProvider } from "react-scroll-section";

import { loadOrdersSummary } from "util/orderUtils";

import { logError } from "apis/utilAPIs";
import { useQuery } from "@tanstack/react-query";
import { getMenu } from "apis/menuAPIs";
import HomeDialog from "./components/HomeDialog";

export function useIsMounted() {
  const isMounted = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    return () => (isMounted.current = false);
  }, []);

  return isMounted;
}

// Wrapper to defer ScrollingProvider initialization to avoid setState during render
const ScrollingProviderWrapper = ({ children, offset }) => {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Use requestAnimationFrame to ensure we're past the initial render cycle
    // Double RAF to be extra safe
    const rafId1 = requestAnimationFrame(() => {
      const rafId2 = requestAnimationFrame(() => {
        setIsReady(true);
      });
    });
    
    return () => {
      cancelAnimationFrame(rafId1);
    };
  }, []);

  if (!isReady) {
    return <>{children}</>;
  }

  return <ScrollingProvider offset={offset}>{children}</ScrollingProvider>;
};

// Simplified styling for better Safari compatibility
const styles = {
  wrapper: {
    width: "100%",
    maxWidth: "100%",
    background: "#f5f7fa",
  },
  container: {
    width: "100%",
    maxWidth: "100%",
    background: "#f5f7fa",
    paddingBottom: 4,
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    background: "#f5f7fa",
    width: "100%",
    maxWidth: "100%",
    overflowX: "hidden",
  },
  loadingSpinner: {
    position: "fixed",
    zIndex: 1000,
    color: "primary.main",
  },
  errorContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    padding: 3,
    background: "#f5f7fa",
    width: "100%",
    maxWidth: "100%",
    overflowX: "hidden",
  },
  errorMessage: {
    marginBottom: 3,
    textAlign: "center",
    color: "error.main",
    fontWeight: 600,
  },
  retryButton: {
    borderRadius: 3,
    textTransform: "none",
    fontWeight: 600,
    px: 4,
    py: 1.5,
    background: "#667eea",
    color: "white",
    "&:hover": {
      background: "#5a6fd8",
    },
  },
  searchResults: {
    padding: { xs: 1, sm: 2 },
    background: "#ffffff",
    borderRadius: { xs: 0, sm: 2 },
    margin: { xs: 0, sm: 1 },
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    width: "100%",
    maxWidth: "100%",
    overflowX: "hidden",
  },
};

const OnlineMenu = (props) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [filteredDishes, setFilteredDishes] = useState([]);
  const [tableIdDisplay, setTableIdDisplay] = useState("");
  const [restaurantName, setRestaurantName] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [dialogSettings, setDialogSettings] = useState({
    isDialogShown: false,
    dialogContent: "",
    dialogTitle: "",
  });

  const updateTheMemberId = (memberId) => dispatch(updateMemberId(memberId));

  const getUrlParam = (key) => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(key);
  };

  const [ordersSummary, setOrdersSummary] = useState(null);
  const savedOrders = useSelector((state) => state.order.ordersSummary);
  const filteredItems = useSelector((state) => state.menu.filterItems);
  const queryWord = useSelector((state) => state.menu.search);
  const id = props.match.params.menuId;
  const orderId = props.match.params.orderId;
  const tableId = props.match.params.tableId;
  const shortUrlCode = getUrlParam("shortUrlCode");
  // console.log('shortUrlCode', shortUrlCode);
  // const showError = (msg) => dispatch(showErrorSnackbar(msg));

  const checkInactiveMenu = (restaurantId) => {
    //console.log('restaurantId', restaurantId);
    let dbRef =  ref(database, `inactiveMenus/${restaurantId}`);
    
  
    const unsubscribe = onValue(dbRef, (snapshot) => {
      // console.log('snapshot', snapshot.val());
      if (snapshot && snapshot.val() && snapshot.val().dishIds) {
        const inactiveIds = snapshot.val().dishIds;
        setFilteredDishes(inactiveIds);
        updateHideMenuItems(inactiveIds);
      } else {
        setFilteredDishes([]);
        updateHideMenuItems([]);
      }
    });
    return unsubscribe;
  };

  const checkDialog = (restaurantId) => {
    let refMenuSettings = ref(database, `menuSettings/${restaurantId}`);
    const unsubscribe = onValue(refMenuSettings, (snapshot) => {
      try {
        if (
          snapshot &&
          snapshot.val() &&
          snapshot.val().showDialog !== undefined &&
          snapshot.val().showDialog !== null
        ) {
          // console.log('snapshot.val()', snapshot.val());
          const isDialogShown = snapshot.val().showDialog;
          const content = snapshot.val().dialogText;
          const title = snapshot.val().dialogTitle;
          const dialogVersion = snapshot.val().dialogVersion;

          const currentDialogVersion = sessionStorage.getItem("dialogVersion");
          const lastShownTimestamp = sessionStorage.getItem('dialogLastShownTime');
          const currentTime = Date.now();
          const fiveMinutesInMs = 5 * 60 * 1000;
          if (currentDialogVersion && currentDialogVersion === dialogVersion && lastShownTimestamp && currentTime - lastShownTimestamp < fiveMinutesInMs) {
            setDialogSettings({
              isDialogShown: false,
              dialogContent: "",
              dialogTitle: "",
            });
            return;
          }

          setDialogSettings({
            isDialogShown,
            dialogContent: content,
            dialogTitle: title,
            dialogVersion: dialogVersion,
          });
        } else {
          setDialogSettings({
            isDialogShown: false,
            dialogContent: "",
            dialogTitle: "",
          });
        }
      } catch (e) {
        // Safari private mode can throw errors on sessionStorage access
        console.warn('Dialog check failed:', e);
        setDialogSettings({
          isDialogShown: false,
          dialogContent: "",
          dialogTitle: "",
        });
      }
    });
    return unsubscribe;
  };

  useEffect(() => {
    const restaurantId = decodeRestaurantId(id);
    const unsubscribe = checkInactiveMenu(restaurantId);
    const memberId = new URLSearchParams(window.location.search).get(
      "memberId"
    );
    updateTheMemberId(memberId);
    
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [id]);

  useEffect(() => {
    if (window && window.parent) {
      const redirectResultType = getUrlParam("type");
      if (redirectResultType === "cancel" || redirectResultType === "error") {
        if (redirectResultType === "error") {
          alert("Payment failed");
        }
        window.parent.postMessage(
          { type: "payment_failure", success: true },
          "*"
        );
      }
    }
  }, []); // Only run once on mount

  const loadCategoriesSuccess = (categoryList) =>
    dispatch(fetchMenuCategorySuccess(categoryList));
  const updateRestaurantId = (restaurantId) =>
    dispatch(setRestaurantId(restaurantId));

  const setCategory = (idx) => dispatch(selectedCategory(idx));

  const loadCart = (restaurantId) => dispatch(loadPreviousCart(restaurantId));

  const categories = useSelector((state) => state.menu.categories);
  const updateHideMenuItems = (items) => dispatch(updateHideDishes(items));

  useEffect(() => {
    if (savedOrders) {
      setOrdersSummary(savedOrders);
    } else {
      const orders = loadOrdersSummary(id);
      setOrdersSummary(orders);
    }
  }, [savedOrders, id]);

  useEffect(() => {
    if (orderId && id) {
      loadCart(id);
    }
  }, [orderId, id]);

  // Restore scroll position when returning from dish detail
  useEffect(() => {
    try {
      const savedScrollPosition = sessionStorage.getItem('menuScrollPosition');
      if (savedScrollPosition) {
        // Use a timeout to ensure the page has fully rendered
        setTimeout(() => {
          window.scrollTo({
            top: parseInt(savedScrollPosition, 10),
            behavior: 'auto' // Use 'auto' for instant scroll, not smooth
          });
          // Clear the saved position after restoring
          sessionStorage.removeItem('menuScrollPosition');
          
          // Clean up the restore time after a delay (after Intersection Observer settles)
          setTimeout(() => {
            sessionStorage.removeItem('menuScrollRestoreTime');
          }, 2500);
        }, 100);
      }
    } catch (e) {
      // Safari private mode can throw errors on sessionStorage access
      console.warn('sessionStorage access failed:', e);
    }
  }, [categories]);

  const isMounted = useIsMounted();

  const processMenuData = (menuData) => {
    if (!menuData) {
      return;
    }

    if (menuData.restaurant && menuData.restaurant.redirectUrl && menuData.tableId !== 'onlineMenu') {
      window.location.assign(menuData.restaurant.redirectUrl);
      return;
    }
    if (menuData.restaurant && menuData.restaurant.name) {
      setRestaurantName(menuData.restaurant.name);
    }
    setTableIdDisplay(menuData.tableId);
    const menu = menuData.menu;
    if (!menu || !menuData.restaurant || !menu.categories) {
      return;
    }

    if (isMounted.current) {
      //console.log('response.data.restaurant', response.data.restaurant);

      const sortedCategories = menu.categories
        .sort((a, b) => (a.sort < b.sort ? -1 : 1))
        .filter((category) => filterVisibility(category));

      loadCategoriesSuccess({
        data: sortedCategories,
        restaurant: menuData.restaurant,
        restaurantId: menuData.restaurant.restaurantId,
        tableId: menuData.tableId,
      });
      if (sortedCategories.length > 0) {
        setCategory(0);
      }
    } else {
      loadCategoriesSuccess({
        data: [],
        restaurant: menuData.restaurant,
        restaurantId: menuData.restaurant.restaurantId,
        tableId: menuData.restaurant.tableId,
      });
    }
  };

  useEffect(() => {
    const restaurantId = decodeRestaurantId(id);

    updateRestaurantId({ restaurantId: restaurantId, tableId: tableId });
  }, [id, tableId]);

  const { isLoading, error, refetch } = useQuery({
    queryKey: ["menu", id, tableId],
    queryFn: () => getMenu(id, tableId, shortUrlCode, processMenuData),
    retry: 3, // retry failed requests up to 3 times
    retryDelay: 1000, // wait 1 second between retries
    staleTime: 100000, // cache data for 10 minutes
    refetchOnWindowFocus: false, // don't refetch when the window regains focus
    suspense: false, // use suspense mode
    timeout: 10000,
  });

  // Remove this useEffect - refetch is automatically triggered by react-query on mount
  // Having this causes an extra unnecessary refetch
  // useEffect(() => {
  //   refetch();
  // }, []);

  useEffect(() => {
    const restaurantId = decodeRestaurantId(id);
    const unsubscribe = checkDialog(restaurantId);
    
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [id]);

  useEffect(() => {
    setShowDialog(dialogSettings.isDialogShown);
  }, [dialogSettings]);

  if (error) {
    const info = {
      restaurantId: id,
      tableId: tableId,
      responseMessage:
        error.response && error.response.data
          ? error.response.data.message
          : "",
      responseStatus:
        error.response && error.response.data ? error.response.data.status : "",
    };

    const errorData = {
      title: "Online menu Error",
      message: `${error.message}, info: ${JSON.stringify(info)}`,
    };

    logError(errorData);

    return (
      <Box sx={styles.errorContainer}>
        <Box sx={{ textAlign: 'center', maxWidth: 400 }}>
          <Typography variant="h5" sx={styles.errorMessage} gutterBottom>
            Oops! Something went wrong
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            We're having trouble loading the menu. Please check your connection and try again.
          </Typography>
          <Button
            sx={styles.retryButton}
            onClick={() => window.location.reload()}
            size="large"
          >
            Try Again
          </Button>
        </Box>
      </Box>
    );
  }

  if (isLoading || !categories || categories.length === 0) {
    return (
      <Box sx={styles.loadingContainer}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress 
            sx={styles.loadingSpinner} 
            size={60}
            thickness={4}
          />
          <Typography 
            variant="h6" 
            sx={{ 
              marginTop: 3, 
              color: 'text.secondary',
              fontWeight: 500 
            }}
          >
            Loading delicious menu...
          </Typography>
        </Box>
        {dialogSettings ? (
          <HomeDialog
            open={showDialog}
            title={dialogSettings.dialogTitle}
            content={dialogSettings.dialogContent}
            onClose={() => {
              setShowDialog(false);
              try {
                sessionStorage.setItem("dialogVersion", dialogSettings.dialogVersion);
                sessionStorage.setItem('dialogLastShownTime', Date.now().toString());
              } catch (e) {
                console.warn('Failed to save dialog state:', e);
              }
            }}
          />
        ) : (
          <></>
        )}
      </Box>
    );
  } else {
    return (
      <Box sx={styles.wrapper}>
        <ScrollingProviderWrapper offset={-180}>
          <DynamicCategories
            key={categories.length}
            categories={categories}
            tableIdDisplay={tableIdDisplay}
            menuId={id}
            tableId={tableId}
            restaurantName={restaurantName}
            queryWord={queryWord}
            ordersSummary={ordersSummary}
          />

          <Box sx={styles.container}>
            {filteredItems && filteredItems.length > 0 ? (
              <Box sx={styles.searchResults}>
                <Typography
                  variant="h6"
                  sx={{
                    marginBottom: 2,
                    fontWeight: 600,
                    color: 'text.primary',
                    textAlign: 'center'
                  }}
                >
                  Search Results ({filteredItems.length})
                </Typography>
                {filteredItems
                  .filter((dish) => {
                    if (filteredDishes.indexOf(dish.id) > -1) {
                      return false;
                    }
                    return filterVisibility(dish);
                  })
                  .map((item) => {
                    return <Dish key={item.id} item={item} />;
                  })}
              </Box>
            ) : (
              <Box>
                <CategorySections
                  key={id}
                  categories={categories}
                  restaurantId={id}
                />
              </Box>
            )}
          </Box>
        </ScrollingProviderWrapper>

        {dialogSettings ? (
          <HomeDialog
            open={showDialog}
            title={dialogSettings.dialogTitle}
            content={dialogSettings.dialogContent}
            onClose={() => {
              setShowDialog(false);
              try {
                sessionStorage.setItem("dialogVersion", dialogSettings.dialogVersion);
                sessionStorage.setItem('dialogLastShownTime', Date.now().toString());
              } catch (e) {
                console.warn('Failed to save dialog state:', e);
              }
            }}
          />
        ) : (
          <></>
        )}
      </Box>
    );
  }
};

export default OnlineMenu;
